import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getIdea,
  listCheckins,
  diagnose,
  toggleAction,
  createCheckin,
  updateCheckin,
  deleteIdea,
  waitForDiagnosis,
  createShareLink,
  revokeShareLink,
} from "../lib/api";
import AppShell from "./AppShell";
import { track } from "@/lib/track";

function verdictStyle(v) {
  if (v === "pursue")
    return { badge: "badge-pursue", text: "verdict-pursue", label: "PURSUE" };
  if (v === "pivot")
    return { badge: "badge-pivot", text: "verdict-pivot", label: "PIVOT" };
  if (v === "kill")
    return { badge: "badge-kill", text: "verdict-kill", label: "KILL" };
  return { badge: "badge-neutral", text: "", label: "-" };
}

function errorMessage(error, fallback = "Diagnosis failed") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (detail?.reason) return detail.reason;
  if (error?.message) return error.message;
  return fallback;
}

function formatDuration(seconds = 0) {
  if (seconds <= 0) return "";
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hours, mins, secs]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

export function getRecheckState(diag, diagnosisLimits, cooldownLeft) {
  if (diagnosisLimits?.unlimited) {
    return {
      canRecheck: true,
      label: !diag ? "Get my verdict" : "Re-diagnose now",
      blockedMessage: "",
    };
  }
  const completedActions =
    diag?.this_week_actions?.filter((a) => a.done).length || 0;
  const weeklyRemaining = diagnosisLimits?.weekly_remaining ?? 1;
  const canRecheck =
    !diag || (completedActions > 0 && cooldownLeft <= 0 && weeklyRemaining > 0);
  const label = !diag
    ? "Get my verdict"
    : completedActions === 0
      ? "Complete 1 task to unlock"
      : weeklyRemaining <= 0
        ? "Come back after making progress"
        : cooldownLeft > 0
          ? `Re-diagnose in ${formatDuration(cooldownLeft)}`
          : "Re-diagnose now";
  const blockedMessage =
    !diag || canRecheck
      ? ""
      : completedActions === 0
        ? "Complete 1 task to unlock."
        : weeklyRemaining <= 0
          ? "Come back after making progress."
          : cooldownLeft > 0
            ? `Wait ${Math.max(1, Math.ceil(cooldownLeft / 60))}m before re-diagnosing.`
            : "This only improves if your situation changes. Go execute.";
  return { canRecheck, label, blockedMessage };
}

function ScoreRow({ label, val, reason, testId, invert = false }) {
  const v = val ?? 0;
  const color = invert
    ? v >= 70
      ? "#ef4444"
      : v >= 40
        ? "#f59e0b"
        : "#059669"
    : v >= 70
      ? "#059669"
      : v >= 40
        ? "#f59e0b"
        : "#ef4444";
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-lg font-medium tracking-tight">
            {label}
          </span>
          <span className="font-mono text-xs text-zinc-500">{v}/100</span>
        </div>
      </div>
      <div className="h-[3px] bg-zinc-100 rounded-full overflow-hidden">
        <div
          style={{ width: `${v}%`, background: color }}
          className="h-full transition-all duration-700"
        />
      </div>
      {reason && (
        <div className="text-sm text-zinc-600 mt-2 leading-relaxed">
          {reason}
        </div>
      )}
    </div>
  );
}

function CopyButton({ text, testId, label = "copy" }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
      className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 transition-colors"
    >
      {copied ? "copied ✓" : label}
    </button>
  );
}

function ActionCard({ a, idx, onToggle }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className={`border rounded-md overflow-hidden transition-colors ${a.done ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200 bg-white"}`}
      data-testid={`action-${a.action_id}`}
    >
      <div className="p-5 flex gap-4 items-start">
        <button
          onClick={() => onToggle(a.action_id)}
          data-testid={`action-toggle-${a.action_id}`}
          className={`mt-0.5 w-5 h-5 rounded-sm border flex items-center justify-center transition-all ${a.done ? "bg-emerald-600 border-emerald-600" : "border-zinc-300 hover:border-zinc-950"}`}
          aria-label="Toggle action"
        >
          {a.done && <span className="text-white text-[10px]">✓</span>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono text-xs text-zinc-400">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <span className="badge badge-neutral">{a.effort || "M"}</span>
          </div>
          <div
            className={`font-display text-lg font-medium tracking-tight ${a.done ? "line-through text-zinc-500" : ""}`}
          >
            {a.title}
          </div>
          <div className="text-sm text-zinc-600 mt-1">{a.why}</div>
          <button
            onClick={() => setOpen(!open)}
            className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950 mt-3 transition-colors"
            data-testid={`action-expand-${a.action_id}`}
          >
            {open ? "hide script" : "show script"} →
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-zinc-200 bg-zinc-50 p-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">how - exact script</div>
              <CopyButton text={a.how} testId={`action-copy-${a.action_id}`} />
            </div>
            <div className="font-mono text-sm whitespace-pre-wrap text-zinc-800 leading-relaxed bg-white border border-zinc-200 rounded-md p-4">
              {a.how}
            </div>
          </div>
          {a.assumption && (
            <div>
              <div className="overline mb-2">assumption validated</div>
              <div className="text-sm text-zinc-800 italic font-serif text-base">
                {a.assumption}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CheckinDialog({
  actions,
  diag,
  diagnosisLimits,
  cooldownLeft,
  latestCheckin,
  initialCheckin,
  isEditing,
  onClose,
  onSubmit,
}) {
  const [completed, setCompleted] = useState([]);
  const [whatChanged, setWhatChanged] = useState("");
  const [learnings, setLearnings] = useState("");
  const [blockers, setBlockers] = useState("");
  const [busy, setBusy] = useState(false);
  const previewDiag = diag
    ? {
        ...diag,
        this_week_actions: actions.map((action) => ({
          ...action,
          done: completed.includes(action.action_id),
        })),
      }
    : null;
  const previewRecheck = getRecheckState(
    previewDiag,
    diagnosisLimits,
    cooldownLeft,
  );

  useEffect(() => {
    // For new check-ins always start blank. When editing, load the initial checkin.
    const source = isEditing ? initialCheckin : null;
    const defaultCompleted = actions
      .filter((a) => a.done)
      .map((a) => a.action_id);
    const sourceCompleted = Array.isArray(source?.diagnosis_actions)
      ? source.diagnosis_actions
          .filter((action) => action.done)
          .map((action) => action.action_id)
      : [];
    setCompleted(sourceCompleted.length > 0 ? sourceCompleted : defaultCompleted);
    setWhatChanged(source?.what_changed || "");
    setLearnings(source?.new_learnings || "");
    setBlockers(source?.blockers || "");
  }, [actions, initialCheckin, isEditing]);

  const toggle = (id) => {
    setCompleted((c) =>
      c.includes(id) ? c.filter((x) => x !== id) : [...c, id],
    );
  };

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({
        actions_completed: completed,
        what_changed: whatChanged,
        new_learnings: learnings,
        blockers,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-30 flex items-center justify-center p-4"
      data-testid="checkin-modal"
    >
      <div className="card max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto fade-up">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">weekly check-in</div>
          {!previewRecheck.canRecheck && (
            <div
              className="text-sm text-zinc-500"
              data-testid="checkin-recheck-status"
            >
              {previewRecheck.label}
            </div>
          )}
        </div>
        <h3 className="font-display text-3xl font-semibold tracking-tight mb-2">
          {isEditing ? "Edit latest check-in" : "What actually happened?"}
        </h3>
        <p className="text-zinc-600 mb-8">
          {previewRecheck.canRecheck
            ? "I'll re-diagnose based on the new evidence. Verdict may flip."
            : "Your check-in will still be saved. Re-diagnosis unlocks when the re-diagnosis timer does."}
        </p>

        {/* Removed prefill/start-mode: new check-ins always start blank */}

        <div className="mb-6">
          <div className="overline mb-3">actions completed</div>
          <div className="space-y-2">
            {actions.map((a) => (
              <label
                key={a.action_id}
                className="flex items-start gap-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={completed.includes(a.action_id)}
                  onChange={() => toggle(a.action_id)}
                  data-testid={`checkin-action-${a.action_id}`}
                />
                <span
                  className={`text-sm ${completed.includes(a.action_id) ? "text-zinc-950 font-medium" : "text-zinc-600"}`}
                >
                  {a.title}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <div className="overline mb-2">what changed?</div>
            <textarea
              data-testid="checkin-changed"
              className="input-base"
              rows={3}
              placeholder="User conversations, signups, insights, pivots…"
              value={whatChanged}
              onChange={(e) => setWhatChanged(e.target.value)}
            />
          </div>
          <div>
            <div className="overline mb-2">what did you learn?</div>
            <textarea
              data-testid="checkin-learnings"
              className="input-base"
              rows={3}
              placeholder="The one thing that surprised you. The assumption that broke."
              value={learnings}
              onChange={(e) => setLearnings(e.target.value)}
            />
          </div>
          <div>
            <div className="overline mb-2">blockers (real ones)</div>
            <textarea
              data-testid="checkin-blockers"
              className="input-base"
              rows={2}
              placeholder="What's actually stopping you - not excuses."
              value={blockers}
              onChange={(e) => setBlockers(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="checkin-cancel"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy}
            data-testid="checkin-submit"
          >
            {busy ? (
              <>
                <span className="spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />{" "}
                Kicking off…
              </>
            ) : isEditing ? (
              "Save changes"
            ) : previewRecheck.canRecheck ? (
              "Submit & re-diagnose ->"
            ) : (
              "Submit check-in"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareDialog({ diagnosis, idea, onClose }) {
  const [slug, setSlug] = useState(diagnosis.share_slug || null);
  const [busy, setBusy] = useState(false);
  const url = slug ? `${window.location.origin}/startup-idea/${slug}` : "";
  const tweetText = encodeURIComponent(
    `Got a ${diagnosis.verdict.toUpperCase()} verdict on my idea from Killswitch.\n\n"${diagnosis.verdict_headline}"\n\nHonest AI startup idea validator for founders ↓`,
  );
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(url)}`;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await createShareLink(diagnosis.diagnosis_id);
      setSlug(r.share_slug);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await revokeShareLink(diagnosis.diagnosis_id);
      setSlug(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-30 flex items-center justify-center p-4"
      data-testid="share-modal"
    >
      <div className="card max-w-lg w-full p-8 fade-up">
        <div className="overline mb-3">share the verdict</div>
        <h3 className="font-display text-3xl font-semibold tracking-tight mb-2">
          Wear it publicly.{" "}
          <span className="font-serif italic text-zinc-500">
            It&apos;s honest.
          </span>
        </h3>
        <p className="text-zinc-600 mb-6 text-sm">
          Public read-only link. Shows your verdict, the one thing you&apos;re
          missing, and the scores. Not the private scripts.
        </p>

        {!slug ? (
          <button
            onClick={generate}
            className="btn btn-primary w-full"
            disabled={busy}
            data-testid="share-generate"
          >
            {busy ? "Generating…" : "Generate share link"}
          </button>
        ) : (
          <>
            <div className="flex items-center gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-md mb-4">
              <input
                readOnly
                value={url}
                className="flex-1 bg-transparent font-mono text-sm text-zinc-800 outline-none"
                data-testid="share-url"
              />
              <CopyButton
                text={url}
                testId="share-copy-url"
                label="copy link"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <a
                data-testid="share-tweet"
                href={tweetUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                Share to X
              </a>
              <a
                data-testid="share-linkedin"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                Share to LinkedIn
              </a>
            </div>
            <button
              onClick={revoke}
              className="text-xs text-zinc-500 hover:text-red-600 transition-colors"
              data-testid="share-revoke"
              disabled={busy}
            >
              {busy ? "revoking…" : "revoke link"}
            </button>
          </>
        )}
        <div className="mt-6 flex justify-end">
          <button
            className="btn btn-ghost"
            onClick={onClose}
            data-testid="share-close"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function DiagnosingOverlay({ status }) {
  const steps = [
    "Reading your brief…",
    "Pressure-testing the problem…",
    "Scoring founder-market fit…",
    "Checking willingness to pay…",
    "Scanning competitors…",
    "Drafting this week's actions…",
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % steps.length), 2600);
    return () => clearInterval(t);
  }, [steps.length]);
  return (
    <div
      className="card p-12 text-center fade-up"
      data-testid="diagnosing-overlay"
    >
      <div className="mb-6 flex justify-center">
        <div className="w-10 h-10 border-2 border-zinc-950 border-t-transparent rounded-full spin" />
      </div>
      <div className="overline mb-3">thinking hard · claude sonnet 4.5</div>
      <h3 className="font-display text-3xl font-semibold tracking-tight mb-3">
        Diagnosing your idea
      </h3>
      <p className="text-zinc-600 max-w-md mx-auto">{steps[i]}</p>
      <div className="mt-8 text-xs font-mono text-zinc-400">
        takes 30-90 seconds · stays honest
      </div>
    </div>
  );
}

function addIcsReminder(idea) {
  // Generate a weekly .ics calendar for next Friday 9am
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 Sun .. 6 Sat
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
  const next = new Date(now);
  next.setDate(now.getDate() + daysUntilFriday);
  next.setHours(9, 0, 0, 0);
  const dtstart = next.toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
  const end = new Date(next.getTime() + 30 * 60 * 1000);
  const dtend = end.toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
  const uid = `${idea.idea_id}@killswitch.app`;
  const url = `${window.location.origin}/ideas/${idea.idea_id}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Killswitch//Weekly Check-in//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstart}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    "RRULE:FREQ=WEEKLY;BYDAY=FR",
    `SUMMARY:Killswitch weekly check-in - ${idea.title}`,
    `DESCRIPTION:Honest progress check on your idea. Open: ${url}`,
    `URL:${url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `killswitch-${idea.title.replace(/\s+/g, "-").toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function IdeaDetail() {
  const { idea_id } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [checkinsHasMore, setCheckinsHasMore] = useState(false);
  const [checkinsCursor, setCheckinsCursor] = useState(null);
  const [checkinsLoadingMore, setCheckinsLoadingMore] = useState(false);
  const [expandedCheckinId, setExpandedCheckinId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [editingCheckin, setEditingCheckin] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("diagnosis");
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const pollingRef = useRef(null);

  const load = async () => {
    try {
      const [d, checkinPage] = await Promise.all([
        getIdea(idea_id),
        listCheckins(idea_id, { limit: 20 }),
      ]);
      setData(d);
      setCheckins(checkinPage?.items || []);
      setExpandedCheckinId(checkinPage?.items?.[0]?.checkin_id || null);
      setCheckinsHasMore(Boolean(checkinPage?.has_more));
      setCheckinsCursor(checkinPage?.next_before || null);
      return d;
    } finally {
      setLoading(false);
    }
  };

  const loadMoreCheckins = async () => {
    if (!checkinsHasMore || checkinsLoadingMore || !checkinsCursor) return;
    setCheckinsLoadingMore(true);
    try {
      const page = await listCheckins(idea_id, {
        limit: 20,
        before: checkinsCursor,
      });
      setCheckins((prev) => [...prev, ...(page?.items || [])]);
      setCheckinsHasMore(Boolean(page?.has_more));
      setCheckinsCursor(page?.next_before || null);
    } finally {
      setCheckinsLoadingMore(false);
    }
  };

  useEffect(() => {
    load().then((d) => {
      if (d?.latest_diagnosis) {
        track("result_viewed");
      }
    });
  }, [idea_id]);

  useEffect(() => {
    const nextCooldown =
      data?.diagnosis_limits?.cooldown_remaining_seconds || 0;
    setCooldownLeft(nextCooldown);
  }, [data?.diagnosis_limits?.cooldown_remaining_seconds]);

  useEffect(() => {
    if (cooldownLeft <= 0) return undefined;
    const interval = setInterval(() => {
      setCooldownLeft((current) => Math.max(current - 1, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownLeft]);

  useEffect(() => {
    if (
      !data ||
      cooldownLeft !== 0 ||
      (data.diagnosis_limits?.cooldown_remaining_seconds || 0) <= 0
    )
      return;
    load();
  }, [cooldownLeft, data]);

  // If we arrived with a job=... in the URL, poll it
  useEffect(() => {
    const jobId = search.get("job");
    if (!jobId || pollingRef.current === jobId) return;
    pollingRef.current = jobId;
    setDiagnosing(true);
    setError("");
    setNotice("");
    (async () => {
      try {
        await waitForDiagnosis(idea_id, jobId);
        await load();
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setDiagnosing(false);
        // clean the url
        search.delete("job");
        setSearch(search, { replace: true });
      }
    })();
  }, [search, idea_id, setSearch]);

  if (loading)
    return (
      <AppShell>
        <div className="overline" data-testid="idea-loading">
          loading…
        </div>
      </AppShell>
    );
  if (!data)
    return (
      <AppShell>
        <div>Not found</div>
      </AppShell>
    );

  const idea = data.idea;
  const diag = data.latest_diagnosis;
  const latestCheckin = checkins[0] || null;
  const diagCheckins = checkins.filter((c) => !!c.diagnosis_id);
  const activeCheckin = diag
    ? checkins.find((c) => c.diagnosis_id === diag.diagnosis_id) || null
    : null;
  const style = verdictStyle(diag?.verdict);
  const { canRecheck, label: recheckLabel } = getRecheckState(
    diag,
    data.diagnosis_limits,
    cooldownLeft,
  );

  const runDiagnose = async () => {
    setDiagnosing(true);
    setError("");
    setNotice("");
    try {
      const job = await diagnose(idea_id);
      await waitForDiagnosis(idea_id, job.job_id);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setDiagnosing(false);
    }
  };

  const onCheckinSubmit = async (payload) => {
    setError("");
    setNotice("");
    try {
      if (editingCheckin?.checkin_id) {
        await updateCheckin(idea_id, editingCheckin.checkin_id, payload);
      } else {
        await createCheckin(idea_id, payload);
      }
    } catch (e) {
      setError(errorMessage(e, "Check-in failed"));
      return;
    }

    setEditingCheckin(null);
    setCheckinOpen(false);
    setNotice(
      editingCheckin
        ? "Latest check-in updated."
        : "Weekly check-in saved.",
    );

    try {
      const refreshed = await load();
      const nextCooldownLeft =
        refreshed?.diagnosis_limits?.cooldown_remaining_seconds || 0;
      const nextState = getRecheckState(
        refreshed?.latest_diagnosis,
        refreshed?.diagnosis_limits,
        nextCooldownLeft,
      );
      if (!editingCheckin && nextState.canRecheck) {
        await runDiagnose();
        return;
      }
      if (nextState.label) {
        setNotice(
          editingCheckin
            ? "Latest check-in updated."
            : `Weekly check-in saved. ${nextState.label}`,
        );
      }
    } catch (e) {
      setNotice(editingCheckin ? "Latest check-in updated." : "Weekly check-in saved.");
      setError(
        errorMessage(e, "Saved your check-in, but couldn't refresh the page"),
      );
    }
  };

  const onToggle = async (aid) => {
    try {
      const r = await toggleAction(idea_id, aid);
      setData((d) => ({
        ...d,
        latest_diagnosis: {
          ...d.latest_diagnosis,
          this_week_actions: r.actions,
        },
      }));
    } catch {}
  };

  const onDelete = async () => {
    if (
      !window.confirm(
        "Archive this idea? It will disappear from your dashboard.",
      )
    )
      return;
    await deleteIdea(idea_id);
    navigate("/dashboard", { replace: true });
  };

  const hasPending = diagnosing || !!search.get("job");

  return (
    <AppShell>
      <div className="fade-up">
        {/* Header */}
        <div className="mb-10 no-print">
          <button
            onClick={() => navigate("/dashboard")}
            className="overline hover:text-zinc-950 transition-colors mb-5"
            data-testid="back-btn"
          >
            ← all ideas
          </button>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`badge ${style.badge}`}
                  data-testid="verdict-badge"
                >
                  {style.label}
                </span>
                {idea.streak > 0 && (
                  <span
                    className="font-mono text-xs text-zinc-500"
                    data-testid="streak-badge"
                  >
                    streak · {String(idea.streak).padStart(2, "0")}
                  </span>
                )}
              </div>
              <h1
                className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter leading-tight mb-2"
                data-testid="idea-title"
              >
                {idea.title}
              </h1>
              <p className="text-lg text-zinc-600">{idea.one_liner}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              {!hasPending && (
                <>
                  <button
                    onClick={() => {
                      // Open the check-in attached to the current latest diagnosis.
                      // If none exists yet, start a new blank check-in for that diagnosis.
                      setEditingCheckin(activeCheckin);
                      setCheckinOpen(true);
                    }}
                    className="btn btn-primary"
                    data-testid="weekly-checkin-btn"
                  >
                    Weekly check-in
                  </button>
                </>
              )}
              {diag && !hasPending && (
                <>
                  <button
                    onClick={() => setShareOpen(true)}
                    className="btn btn-secondary"
                    data-testid="share-btn"
                  >
                    Share
                  </button>
                  {/* <button onClick={() => addIcsReminder(idea)} className="btn btn-secondary" data-testid="calendar-btn">+ Calendar</button> */}
                  <button
                    onClick={() => window.print()}
                    className="btn btn-secondary"
                    data-testid="print-btn"
                  >
                    Export PDF
                  </button>
                </>
              )}
              {!hasPending && (
                <button
                  onClick={runDiagnose}
                  className="btn btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed min-w-[12rem] justify-center whitespace-nowrap"
                  disabled={diagnosing || !canRecheck}
                  data-testid="rediagnose-btn"
                >
                  {recheckLabel}
                </button>
              )}
              <button
                onClick={onDelete}
                className="btn btn-ghost text-xs text-red-600"
                data-testid="delete-idea-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            className="card p-4 mb-6 border-red-200 bg-red-50 text-red-800 text-sm"
            data-testid="idea-error"
          >
            {error}
          </div>
        )}

        {notice && (
          <div
            className="card p-4 mb-6 border-emerald-200 bg-emerald-50 text-emerald-800 text-sm"
            data-testid="idea-notice"
          >
            {notice}
          </div>
        )}

        {hasPending && <DiagnosingOverlay />}

        {/* Tabs */}
        {!hasPending && (data.diagnoses.length > 0 || checkins.length > 0) && (
          <div className="border-b border-zinc-200 mb-8 flex gap-6 no-print">
            <TabBtn
              k="diagnosis"
              cur={tab}
              onClick={setTab}
              label="Latest diagnosis"
            />
            <TabBtn
              k="evolution"
              cur={tab}
              onClick={setTab}
              label={`Evolution (${data.diagnoses.length})`}
            />
            <TabBtn
              k="checkins"
              cur={tab}
              onClick={setTab}
              label={`Check-ins (${diagCheckins.length})`}
            />
          </div>
        )}

        {!hasPending && tab === "diagnosis" && diag && (
          <div
            className="grid lg:grid-cols-12 gap-8 stagger print-block"
            data-testid="diagnosis-view"
          >
            {/* Left: diagnosis */}
            <div className="lg:col-span-7 space-y-8">
              <section className="card p-8" data-testid="verdict-card">
                <div className="overline mb-4">the call</div>
                <div
                  className={`font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight mb-6 ${style.text}`}
                >
                  {diag.verdict_headline}
                </div>
                <div className="hairline mb-5" />
                <div className="overline mb-2">diagnosis</div>
                <p className="text-base text-zinc-800 leading-relaxed mb-6">
                  {diag.diagnosis}
                </p>
                <div className="overline mb-2">what you&apos;re missing</div>
                <p
                  className="font-serif italic text-xl leading-snug text-zinc-950"
                  data-testid="key-insight"
                >
                  {diag.key_insight}
                </p>
              </section>

              {/* This week actions */}
              <section>
                <div className="flex items-baseline justify-between mb-5">
                  <div>
                    <div className="overline mb-1">this week</div>
                    <h2 className="font-display text-2xl font-semibold tracking-tight">
                      Do these. Nothing else.
                    </h2>
                  </div>
                  <span className="font-mono text-xs text-zinc-500">
                    {diag.this_week_actions?.filter((a) => a.done).length}/
                    {diag.this_week_actions?.length} done
                  </span>
                </div>
                <div className="space-y-3" data-testid="actions-list">
                  {(diag.this_week_actions || []).map((a, i) => (
                    <ActionCard
                      key={a.action_id}
                      a={a}
                      idx={i}
                      onToggle={onToggle}
                    />
                  ))}
                </div>
              </section>
            </div>

            {/* Right: sidebar */}
            <div className="lg:col-span-5 space-y-6">
              <div className="card p-6 space-y-5" data-testid="scores-card">
                <div className="overline">the scores</div>
                <ScoreRow
                  label="Founder-market fit"
                  val={diag.fmf_score}
                  reason={diag.fmf_reason}
                  testId="score-fmf"
                />
                <ScoreRow
                  label="Willingness to pay"
                  val={diag.wtp_score}
                  reason={diag.wtp_reason}
                  testId="score-wtp"
                />
                <ScoreRow
                  label="Momentum"
                  val={diag.momentum_score}
                  reason={diag.momentum_reason}
                  testId="score-momentum"
                />
                <ScoreRow
                  label="Execution risk"
                  val={diag.execution_risk}
                  reason={diag.execution_risk_reason}
                  testId="score-risk"
                  invert
                />
              </div>

              <div className="card p-6" data-testid="competitor-card">
                <div className="overline mb-3">competitor signal</div>
                <p className="text-sm text-zinc-800 leading-relaxed">
                  {diag.competitor_signal}
                </p>
              </div>

              <div className="card p-6" data-testid="kill-conditions-card">
                <div className="overline mb-3">kill conditions</div>
                <ul className="space-y-2">
                  {(diag.kill_conditions || []).map((k, i) => (
                    <li
                      key={i}
                      className="text-sm text-zinc-800 flex gap-2 leading-relaxed"
                    >
                      <span className="text-red-500 font-mono flex-shrink-0">
                        ×
                      </span>
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6" data-testid="momentum-signals-card">
                <div className="overline mb-3">momentum signals to watch</div>
                <ul className="space-y-2">
                  {(diag.momentum_signals || []).map((k, i) => (
                    <li
                      key={i}
                      className="text-sm text-zinc-800 flex gap-2 leading-relaxed"
                    >
                      <span className="text-emerald-600 font-mono flex-shrink-0">
                        ↗
                      </span>
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {!hasPending && tab === "diagnosis" && !diag && (
          <div className="card p-12 text-center">
            <div className="overline mb-3">no diagnosis yet</div>
            <button
              className="btn btn-primary"
              onClick={runDiagnose}
              disabled={diagnosing}
              data-testid="first-diagnose-btn"
            >
              {diagnosing ? "Diagnosing…" : "Get my verdict →"}
            </button>
          </div>
        )}

        {!hasPending && tab === "checkins" && (
          <div className="space-y-5">
            <section>
              <div className="overline mb-2">weekly check-ins</div>
              <div className="space-y-3" data-testid="checkin-history-list">
                {diagCheckins.length === 0 && (
                  <div className="card p-5 text-sm text-zinc-600">
                    No check-ins yet.
                  </div>
                )}
                {diagCheckins.map((c, i) => {
                  const date = new Date(c.created_at_ist || c.created_at);
                  const isOpen = expandedCheckinId === c.checkin_id;
                  return (
                    <div
                      key={c.checkin_id}
                      className={`card p-5 transition-colors ${isOpen ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}
                      data-testid={`checkin-history-${i}`}
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 text-left"
                        onClick={() =>
                          setExpandedCheckinId((current) =>
                            current === c.checkin_id ? null : c.checkin_id,
                          )
                        }
                        aria-expanded={isOpen}
                        data-testid={`checkin-toggle-${i}`}
                      >
                        <div>
                          <span className="font-mono text-xs text-zinc-500">
                            #{diagCheckins.length - i} ·{" "}
                            {date.toLocaleDateString("en-IN", {
                              timeZone: "Asia/Kolkata",
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-zinc-500">
                          <span className="font-mono text-xs uppercase tracking-[0.15em]">
                            {isOpen ? "collapse" : "expand"}
                          </span>
                          <span className="text-sm leading-none">
                            {isOpen ? "⌃" : "⌄"}
                          </span>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="mt-4 space-y-3 text-sm">
                          <div className="grid gap-3 sm:grid-cols-3">
                            <div>
                              <div className="overline mb-1">what changed</div>
                              <p className="text-zinc-800 whitespace-pre-wrap leading-relaxed">
                                {c.what_changed || "-"}
                              </p>
                            </div>
                            <div>
                              <div className="overline mb-1">learnings</div>
                              <p className="text-zinc-800 whitespace-pre-wrap leading-relaxed">
                                {c.new_learnings || "-"}
                              </p>
                            </div>
                            <div>
                              <div className="overline mb-1">blockers</div>
                              <p className="text-zinc-800 whitespace-pre-wrap leading-relaxed">
                                {c.blockers || "-"}
                              </p>
                            </div>
                          </div>
                          {Array.isArray(c.diagnosis_actions) && c.diagnosis_actions.length > 0 && (
                            <div>
                              <div className="overline mb-1">actions</div>
                              <div className="space-y-2">
                                {c.diagnosis_actions.map((action) => (
                                  <div
                                    key={action.action_id}
                                    className={`rounded-lg border px-4 py-3 shadow-sm   ${action.done ? "bg-emerald-50/30 border-emerald-200" : "bg-red-50/30 border-red-200"}`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-zinc-900 leading-snug">
                                          {action.title}
                                        </div>
                                        {action.why && (
                                          <div className="mt-1 text-xs text-zinc-500 leading-relaxed">
                                            {action.why}
                                          </div>
                                        )}
                                      </div>
                                      <span
                                        className={`inline-flex flex-shrink-0 items-center rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${action.done ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                                      >
                                        {action.done ? "complete" : "incomplete"}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {checkinsHasMore && (
                <div className="mt-4">
                  <button
                    className="btn btn-secondary"
                    onClick={loadMoreCheckins}
                    disabled={checkinsLoadingMore}
                    data-testid="checkin-history-load-more"
                  >
                    {checkinsLoadingMore ? "Loading..." : "Load older check-ins"}
                  </button>
                </div>
              )}
            </section>
          </div>
        )}

        {!hasPending && tab === "evolution" && (
          <div className="space-y-8" data-testid="evolution-list">
            <section>
              <div className="overline mb-3">diagnosis evolution</div>
              <div className="space-y-4">
                {data.diagnoses.map((d, i) => {
                  const s = verdictStyle(d.verdict);
                  const date = new Date(d.created_at_ist || d.created_at);
                  return (
                    <div
                      key={d.diagnosis_id}
                      className="card p-6"
                      data-testid={`evolution-${i}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-mono text-xs text-zinc-500">
                          #{data.diagnoses.length - i} ·{" "}
                          {date.toLocaleDateString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          })}
                        </span>
                        <span className={`badge ${s.badge}`}>{s.label}</span>
                        <span className="font-mono text-xs text-zinc-500">
                          FMF {d.fmf_score} · WTP {d.wtp_score} · MOM{" "}
                          {d.momentum_score}
                        </span>
                      </div>
                      <div
                        className={`font-display text-xl font-medium tracking-tight leading-tight mb-2 ${s.text}`}
                      >
                        {d.verdict_headline}
                      </div>
                      <p className="text-sm text-zinc-600 leading-relaxed font-serif italic">
                        {d.key_insight}
                      </p>
                    </div>
                  );
                })}
                {data.diagnoses.length === 0 && (
                  <div className="card p-6 text-sm text-zinc-600">
                    No diagnoses yet.
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      {checkinOpen && (
        <CheckinDialog
          actions={diag?.this_week_actions || []}
          diag={diag}
          diagnosisLimits={data.diagnosis_limits}
          cooldownLeft={cooldownLeft}
          latestCheckin={latestCheckin}
          initialCheckin={editingCheckin}
          isEditing={Boolean(editingCheckin)}
          onClose={() => {
            setCheckinOpen(false);
            setEditingCheckin(null);
          }}
          onSubmit={onCheckinSubmit}
        />
      )}

      {shareOpen && diag && (
        <ShareDialog
          diagnosis={diag}
          idea={idea}
          onClose={() => setShareOpen(false)}
        />
      )}
    </AppShell>
  );
}

function TabBtn({ k, cur, onClick, label }) {
  const active = cur === k;
  return (
    <button
      onClick={() => onClick(k)}
      data-testid={`tab-${k}`}
      className={`py-3 text-sm font-medium border-b-2 transition-colors ${active ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}
    >
      {label}
    </button>
  );
}
